import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useHasPermission } from '@/auth/auth-store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  isPricedByTill,
  useLoyaltyCampaignImport,
  useRunLoyaltyCampaignImport,
  type CampaignImportEntry,
  type CampaignImportOutcome,
  type CampaignImportTransition,
} from '@/hooks/use-loyalty-campaign-import';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

/**
 * Which re:form Loyalty campaigns the till prices, and why the rest are not
 * (YUKI-248).
 *
 * The promotions list cannot answer this: a campaign the importer skipped has
 * no promotion row, so it is absent rather than shown as unsupported. That is
 * the whole problem — "the campaign I authored does nothing" had no answer in
 * Back Office, only a line in a server log.
 */

const OUTCOME_LABEL: Record<CampaignImportOutcome, string> = {
  imported: 'Imported',
  updated: 'Updated',
  unchanged: 'Unchanged',
  archived: 'Archived',
  skipped: 'Not priced',
};

/**
 * Why a campaign is not priced, in an operator's words rather than the
 * mapper's. An unknown code falls through to the code itself: a newer backend
 * must show something true, not a blank cell.
 */
const REASON_LABEL: Record<string, string> = {
  accrual_only: 'Gives bonuses only — accrual stays with Loyalty, nothing to price',
  unsupported_model: 'Campaign model the till has no rule for',
  unsupported_benefit: 'Benefit the till has no rule for',
  unsupported_percent: 'Percentage the till cannot express exactly',
  unsupported_condition: 'Condition the till cannot evaluate',
  unsupported_applicability: 'Audience the till cannot resolve',
  unsupported_limits: 'Has a budget or usage cap the till cannot enforce',
  unsupported_field: 'Uses a field the till does not carry',
  unsupported_schedule: 'Schedule the till cannot follow',
  unsupported_rule_schema: 'Rule shape the till does not understand',
  unsupported_scope: 'Scope the till does not support',
  no_catalogue_match: 'Matches no product in this Back Office catalogue',
  conflicts_with: 'Overlaps another campaign; the till cannot choose between them',
  not_active_in_loyalty: 'Not active in Loyalty',
  expired_in_loyalty: 'Expired in Loyalty',
  ended: 'Ended',
  invalid_mapping: 'Could not be mapped',
  invalid_definition: 'Definition rejected as invalid',
  code_taken: 'Its promotion code is already used by a Back Office promotion',
};

const TRIGGER_LABEL: Record<'schedule' | 'manual', string> = {
  schedule: 'Scheduled',
  manual: 'Started by hand',
};

const TRANSITION_LABEL: Record<CampaignImportTransition['kind'], string> = {
  lost: 'No longer priced',
  recovered: 'Priced again',
  changed: 'Reason changed',
  appeared: 'New in feed',
  disappeared: 'Gone from feed',
};

function outcomeVariant(outcome: CampaignImportOutcome) {
  switch (outcome) {
    case 'imported':
    case 'updated':
      return 'success' as const;
    case 'unchanged':
      return 'default' as const;
    case 'archived':
      return 'outline' as const;
    case 'skipped':
    default:
      return 'destructive' as const;
  }
}

export function LoyaltyCampaignImportPage() {
  const t = useT('admin_ui');
  const canRun = useHasPermission('promotions.admin');
  // RL-419: the promotions list links here with `?problems=1`.
  const [params] = useSearchParams();
  const [onlyProblems, setOnlyProblems] = useState(params.get('problems') === '1');
  const { data, isLoading, isError } = useLoyaltyCampaignImport();
  const run = useRunLoyaltyCampaignImport();

  const report = data?.report ?? null;
  const all = report?.campaigns ?? [];
  const transitions = report?.transitions ?? [];
  const problems = all.filter((c) => !isPricedByTill(c));
  const shown = onlyProblems ? problems : all;

  const reasonText = (entry: CampaignImportEntry): string => {
    if (entry.reason === null) return '';
    const fallback = REASON_LABEL[entry.reason] ?? entry.reason;
    return t(`promotions.import.reason.${entry.reason}`, fallback);
  };

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('promotions.import.title', 'Loyalty campaign import')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'promotions.import.subtitle',
              'Every campaign re:form Loyalty offers, and what the till does with it. A campaign that is not priced has no promotion row, so it does not appear on the promotions list at all.',
            )}
          </p>
        </div>
        {canRun && (
          <Button onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending
              ? t('promotions.import.running', 'Importing…')
              : t('promotions.import.run', 'Import now')}
          </Button>
        )}
      </header>

      {run.isError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
          {t('promotions.import.runFailed', 'The import could not be started.')}
        </p>
      )}

      {isLoading ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
          {t('promotions.import.loadFailed', 'The campaign import report could not be loaded.')}
        </p>
      ) : report === null ? (
        <p className="rounded-md border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          {t(
            'promotions.import.neverRun',
            'The import has not run for this tenant yet, so there is nothing to report.',
          )}
        </p>
      ) : (
        <>
          {data?.lastStatus === 'failed' && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
              {t(
                'promotions.import.lastRunFailed',
                'The most recent import run failed. The report below is from the last run that completed.',
              )}
            </p>
          )}
          <dl className="grid grid-cols-2 gap-3 rounded-md border border-border bg-card p-4 text-sm sm:grid-cols-5">
            <div>
              <dt className="text-muted-foreground">
                {t('promotions.import.lastRun', 'Last run')}
              </dt>
              <dd>{new Date(report.runAt).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('promotions.import.trigger', 'Started')}</dt>
              <dd>
                {t(
                  `promotions.import.trigger.${report.trigger}`,
                  TRIGGER_LABEL[report.trigger] ?? report.trigger,
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t('promotions.import.campaigns', 'Campaigns')}
              </dt>
              <dd>{all.length}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t('promotions.import.notPriced', 'Not priced')}
              </dt>
              <dd className={cn(problems.length > 0 && 'font-semibold text-destructive')}>
                {problems.length}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t('promotions.import.registry', 'Registry')}
              </dt>
              <dd className="font-mono text-xs">{report.registry.version}</dd>
            </div>
          </dl>

          {transitions.length > 0 && (
            <section className="rounded-md border border-border bg-card p-4 text-sm">
              <h2 className="font-semibold">
                {t('promotions.import.transitions.title', 'Changes since the previous run')}
              </h2>
              <ul className="mt-2 space-y-1">
                {transitions.map((transition) => (
                  <li key={transition.externalId} className="flex flex-wrap gap-x-2">
                    <span>{transition.name}</span>
                    <Badge variant={transition.kind === 'recovered' ? 'success' : 'outline'}>
                      {t(
                        `promotions.import.transition.${transition.kind}`,
                        TRANSITION_LABEL[transition.kind],
                      )}
                    </Badge>
                    {transition.to?.reason !== null && transition.to?.reason !== undefined && (
                      <span className="text-muted-foreground">
                        {t(
                          `promotions.import.reason.${transition.to.reason}`,
                          REASON_LABEL[transition.to.reason] ?? transition.to.reason,
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!report.complete && (
            <p className="rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
              {t(
                'promotions.import.incomplete',
                'This run did not reach the end of the feed. The next run carries on from where it stopped, so the list below may be short.',
              )}
            </p>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyProblems}
              onChange={(e) => setOnlyProblems(e.target.checked)}
            />
            {t('promotions.import.onlyProblems', 'Show only campaigns the till does not price')}
          </label>

          <div className="rounded-md border border-border bg-card">
            {shown.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground">
                {onlyProblems
                  ? t(
                      'promotions.import.allPriced',
                      'Every campaign in the feed is priced by the till.',
                    )
                  : t('promotions.import.empty', 'The feed held no campaigns on the last run.')}
              </p>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>{t('promotions.import.field.name', 'Campaign')}</TH>
                    <TH>{t('promotions.import.field.model', 'Model')}</TH>
                    <TH>{t('promotions.import.field.outcome', 'Outcome')}</TH>
                    <TH>{t('promotions.import.field.reason', 'Why')}</TH>
                    <TH className="text-right">{t('promotions.field.actions', 'Actions')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {shown.map((c) => (
                    <TR key={c.externalId}>
                      <TD>
                        <div>{c.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {c.externalId}
                        </div>
                      </TD>
                      <TD className="font-mono text-xs">{c.model}</TD>
                      <TD>
                        <Badge variant={outcomeVariant(c.outcome)}>
                          {t(`promotions.import.outcome.${c.outcome}`, OUTCOME_LABEL[c.outcome])}
                        </Badge>
                      </TD>
                      <TD className="text-xs">
                        {reasonText(c)}
                        {c.detail !== null && c.detail !== '' && (
                          <div className="text-muted-foreground">{c.detail}</div>
                        )}
                        {c.notes.length > 0 && (
                          <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                            {c.notes.map((n) => (
                              <li key={n}>{n}</li>
                            ))}
                          </ul>
                        )}
                      </TD>
                      <TD className="text-right">
                        {c.code !== null && (
                          <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>
        </>
      )}

      <p className="text-sm">
        <Link className="text-primary underline" to="/promotions">
          {t('promotions.import.backToList', 'Back to promotions')}
        </Link>
      </p>
    </div>
  );
}
